import { reactive, readonly } from 'vue';
import brandingAPI from 'dashboard/api/nexceo/branding';

// Module-level shared state — survives component unmounts, shared across all instances.
const state = reactive({
  loaded: false,
  brandName: '',
  brandTagline: '',
  primaryColor: '',
  pwaName: '',
  pwaShortName: '',
  assets: {},
});

// Replaces all favicon <link> elements with fresh ones pointing at the uploaded icon.
// Updating href on an existing element is ignored by Chrome once the page has committed
// its favicon — removing and re-appending forces Chrome to re-fetch.
function applyFavicon() {
  const url = state.assets?.favicon;
  if (!url) return;

  document
    .querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']")
    .forEach(l => l.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = url;
  document.head.appendChild(link);

  const shortcut = document.createElement('link');
  shortcut.rel = 'shortcut icon';
  shortcut.href = url;
  document.head.appendChild(shortcut);

  const touchUrl =
    state.assets?.pwa_icon_192 || state.assets?.pwa_icon_512 || url;
  document.querySelectorAll("link[rel='apple-touch-icon']").forEach(l => {
    l.href = touchUrl;
  });
}

function applyThemeColor() {
  const color = state.primaryColor;
  if (!color) return;
  let meta = document.querySelector("meta[name='theme-color']");
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = color;
}

function applyFromData(data) {
  state.brandName = data.brand_name || '';
  state.brandTagline = data.brand_tagline || '';
  state.primaryColor = data.primary_color || '';
  state.pwaName = data.pwa_name || '';
  state.pwaShortName = data.pwa_short_name || '';
  state.assets = data.assets || {};
  state.loaded = true;
  applyFavicon();
  applyThemeColor();
}

export function useNexceoBranding() {
  async function loadBranding() {
    if (state.loaded) return;
    try {
      const { data } = await brandingAPI.get();
      applyFromData(data);
    } catch {
      // branding is optional — fail silently
    }
  }

  function refreshBranding(data) {
    applyFromData(data);
  }

  function invalidate() {
    state.loaded = false;
  }

  return {
    branding: readonly(state),
    loadBranding,
    refreshBranding,
    invalidate,
    applyFavicon,
    applyThemeColor,
  };
}
